// Mobile sandwich-menu entry for "Report a bug". Renders a single button
// row that the user taps to open a centered modal containing the same
// `BugReportPanelContent` the desktop floating pill uses. The dev-mode
// footer is shown when the caller passes the same dev-mode callbacks the
// desktop pill consumes — keeps mobile + desktop at functional parity.
//
// Mounted by `Sidebar.tsx` inside the mobile drawer between
// `BucketSwitcher` and `SidebarGlobalItems` per
// docs/planning/bug_report_placement_v2.md.
//
// CRITICAL — the modal is portaled to `document.body`. When the user taps
// the button, the parent `<aside>` drawer closes via `onAfterAction`. The
// drawer applies `-translate-x-full`, which is a CSS `transform` — that
// makes the aside a containing block for any `position: fixed`
// descendants. If the modal lived inside the aside, it would slide
// off-screen along with the drawer (the user's exact bug report: "doesn't
// open immediately, it actually closes the panel back, makes it seem like
// it failed"). The aside also sets `inert` + `pointer-events-none` when
// closed, locking out interaction even where the modal does render.
// Portaling escapes the aside entirely so the modal pins to the real
// viewport. Dev-sync note 63d7f41e.

import { useState } from "react";
import { createPortal } from "react-dom";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { BugReportPanelContent } from "@/components/common/BugReportPanelContent";
import type { BugReportDevModeSessionInfo } from "@/components/common/BugReportDevModeFooter";
import { useBugReportPanel } from "@/components/common/useBugReportPanel";
import { useBugReportPanelOpenBroadcast } from "@/components/common/useBugReportPanelOpenBroadcast";

export type BugReportMobileEntryProps = {
  mode: PrimaryMode;
  /** Optional dev-mode info — when present, the modal renders the same footer the desktop pill does. */
  devMode?: boolean;
  requestDevMode?: () => void;
  disableDevMode?: () => void;
  getSessionInfo?: () => BugReportDevModeSessionInfo;
  /** Closes the parent drawer once the modal opens. Matches `onAfterAction` shape used by sibling entries. */
  onAfterAction?: () => void;
};

export function BugReportMobileEntry({
  mode,
  devMode,
  requestDevMode,
  disableDevMode,
  getSessionInfo,
  onAfterAction,
}: BugReportMobileEntryProps) {
  const [open, setOpen] = useState(false);
  const panel = useBugReportPanel({ mode, loadOnOpenKey: open });
  // Broadcasts open/close transitions. The dock-side subscriber gates
  // on `source === "desktop"`, so this mobile emit is currently
  // informational only — kept for symmetry / future subscribers.
  useBugReportPanelOpenBroadcast(open, "mobile");

  if (!panel.isAuth) return null;

  const sessionInfo = getSessionInfo ? getSessionInfo() : null;
  const showDevModeFooter =
    panel.canQueueSync && !!requestDevMode && !!disableDevMode;

  function handleOpen() {
    setOpen(true);
    onAfterAction?.();
  }

  return (
    <>
      <div className="border-t border-gray-200 dark:border-gray-800 px-2 py-2">
        <button
          type="button"
          onClick={handleOpen}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-rose-900 hover:bg-rose-50 dark:text-rose-200 dark:hover:bg-rose-950/30"
          aria-label="Report a bug"
        >
          <span aria-hidden className="text-base leading-none">🐛</span>
          <span className="flex-1 text-left">Report a bug</span>
          {panel.openCount > 0 ? (
            <span className="rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0 leading-5">
              {panel.openCount}
            </span>
          ) : null}
        </button>
      </div>

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              data-bug-report-panel="mobile"
              className="fixed inset-0 z-[9300] flex items-end justify-center bg-black/40 backdrop-blur-[1px] sm:items-center"
              role="dialog"
              aria-modal="true"
              aria-label="Report a bug"
              onClick={() => setOpen(false)}
            >
          <div
            className="w-full max-w-md max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 shrink-0 rounded-t-2xl">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Report a Bug</span>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 text-lg leading-none"
                onClick={() => setOpen(false)}
                aria-label="Close report a bug"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto">
              <BugReportPanelContent
                mode={mode}
                description={panel.description}
                onDescriptionChange={panel.setDescription}
                onDictationAppend={panel.appendDictation}
                submitting={panel.submitting}
                submitError={panel.submitError}
                submitDone={panel.submitDone}
                submitDoneDevSync={panel.submitDoneDevSync}
                canQueueSync={panel.canQueueSync}
                isOwnerSync={panel.isOwnerSync}
                queueForDevSync={panel.queueForDevSync}
                setQueueForDevSync={panel.setQueueForDevSync}
                repoPath={panel.repoPath}
                setRepoPath={panel.setRepoPath}
                setGraphicFile={panel.setGraphicFile}
                onSubmit={() => void panel.submitReport()}
                myReports={panel.myReports}
                reportsLoading={panel.reportsLoading}
                openCount={panel.openCount}
                devModeFooter={
                  showDevModeFooter
                    ? {
                        devMode: !!devMode,
                        sessionInfo,
                        onEnable: requestDevMode!,
                        onDisable: disableDevMode!,
                      }
                    : null
                }
              />
            </div>
          </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
